import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Send, Loader2 } from "lucide-react";
import { AdminPageShell } from "@/components/dashboard/AdminPageShell";
import { AdminCursorTable } from "@/components/dashboard/AdminCursorTable";
import { useServerFn } from "@tanstack/react-start";
import { getIndexNowStats, listIndexNowLog } from "@/lib/seo-tabs.functions";

export const Route = createFileRoute("/dashboard/admin/seo-indexnow")({
  head: () => ({ meta: [{ title: "IndexNow · Admin" }, { name: "robots", content: "noindex" }] }),
  component: IndexNowPage,
});

type Stats = Awaited<ReturnType<typeof getIndexNowStats>>;
type Row = Awaited<ReturnType<typeof listIndexNowLog>>["rows"][number];

function IndexNowPage() {
  const fetchStats = useServerFn(getIndexNowStats);
  const fetchList = useServerFn(listIndexNowLog);
  const [stats, setStats] = useState<Stats | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "ok" | "failed">("all");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    fetchStats().then(setStats).catch(() => {});
  }, [fetchStats]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const resetKey = JSON.stringify({ statusFilter, q: debounced });

  return (
    <AdminPageShell
      title="IndexNow"
      description="Push new and updated URLs to Bing, Yandex, and IndexNow partners."
      searchValue={search}
      onSearchChange={setSearch}
      searchPlaceholder="Filter by source"
      stats={[
        { label: "Submitted (24h)", value: stats?.submitted24h.toLocaleString() ?? "—" },
        { label: "Accepted", value: stats?.ok.toLocaleString() ?? "—" },
        { label: "Failed", value: stats?.failed.toLocaleString() ?? "—" },
        {
          label: "Last submission",
          value: stats?.lastSubmission
            ? new Date(stats.lastSubmission).toLocaleString()
            : "—",
        },
      ]}
      primaryAction={{ label: "Submit URLs", icon: Send }}
    >
      <div className="mb-3 flex items-center gap-2">
        {(["all", "ok", "failed"] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`h-8 px-3 text-[12px] border rounded-md ${
              statusFilter === s
                ? "border-primary text-primary font-semibold"
                : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {s === "all" ? "All" : s === "ok" ? "Accepted" : "Failed"}
          </button>
        ))}
      </div>

      <AdminCursorTable<Row, string>
        resetKey={resetKey}
        columns={[
          { label: "Submitted" },
          { label: "Source" },
          { label: "URLs" },
          { label: "Status" },
          { label: "Code" },
          { label: "Error" },
        ]}
        rowKey={(r) => r.id}
        fetchPage={async (cursor) =>
          fetchList({
            data: {
              cursor: cursor as string | null,
              status: statusFilter,
              source: debounced || undefined,
            },
          })
        }
        renderRow={(r) => (
          <>
            <td className="p-3 text-[12px] text-muted-foreground whitespace-nowrap">
              {new Date(r.created_at).toLocaleString()}
            </td>
            <td className="p-3 text-[12px] font-mono">{r.source ?? "—"}</td>
            <td className="p-3 text-[12px]">{r.submitted_count ?? 0}</td>
            <td className="p-3 text-[12px]">
              <span className={r.ok ? "text-emerald-700" : "text-rose-700"}>
                {r.ok ? "ok" : "failed"}
              </span>
            </td>
            <td className="p-3 text-[12px] text-muted-foreground">{r.status_code ?? "—"}</td>
            <td className="p-3 text-[11px] text-muted-foreground max-w-[420px] truncate" title={r.error_message ?? ""}>
              {r.error_message ?? "—"}
            </td>
          </>
        )}
        emptyMessage="No IndexNow submissions yet."
      />
    </AdminPageShell>
  );
}
